import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getPath } from '@state/slices/app/routing/routingSelectors';
import { TripTypes } from '@model/common/tours/trip-types';
import { Page } from '@model/common';
import { TAB_INDEXES } from '@model/search/common';
import { CommonSearchState } from '@model/state/search-forms-state';
import { getSearchType } from '@state/slices/deal-finder/dealFinderSelectors';
import { SearchType } from '@model/iceberg/deal-finder/deal-finder';
import { GlobalAppState } from '@model/state';

// Action types
export enum CommonSearchActionTypes {
  SLICE_NAME = '@SEARCH/COMMON',
  SET_TAB_INDEX = '@SEARCH/COMMON/setTabIndex',
  SET_TAB_INDEX_BY_SEARCH_TYPE = '@SEARCH/COMMON/setTabIndexBySearchType',
  HANDLE_PATH_CHANGE = '@SEARCH/COMMON/handlePathChange'
}

// Initial state
export const INITIAL_COMMON_SEARCH_STATE: CommonSearchState = {
  tabIndex: TAB_INDEXES.HOLIDAYS
};

// Helper function to determine tab index from path
export function getTabIndexFromPath(path: string, searchType?: SearchType, currentTab?: number): number {
  if (path === '/') {
    return currentTab || TAB_INDEXES.HOLIDAYS;
  }
  const tours = [
    TripTypes.ESCORTED_TOURS,
    TripTypes.FLY_DRIVES,
    TripTypes.RAIL_JOURNEYS,
    TripTypes.RIVER_CRUISES,
    TripTypes.WALKING_ROUTES,
    TripTypes.MULTI_CENTRE,
    TripTypes.CRUISES,
    Page.TOURS_SEARCH
  ];

  if (path.includes(TripTypes.PRIVATE_TOURS)) {
    return TAB_INDEXES.PRIVATE_TOURS;
  }

  if (path.includes(TripTypes.RIVER_CRUISES)) {
    return TAB_INDEXES.RIVER_CRUISES;
  }

  if (path.includes(TripTypes.MULTI_CENTRE)) {
    return TAB_INDEXES.MULTI_CENTRE;
  }

  if (path.includes(TripTypes.CRUISES)) {
    return TAB_INDEXES.CRUISES;
  }

  if (tours.some((tour: string) => path.includes(tour))) {
    return TAB_INDEXES.ESCORTED_TOURS;
  }
  if (
    path.includes(Page.SEARCH) &&
    (searchType === SearchType.HOTEL_ONLY || path.includes(`searchType=${SearchType.HOTEL_ONLY}`))
  ) {
    return TAB_INDEXES.HOTEL_ONLY;
  }
  if (path.includes(Page.HOLIDAYS)) {
    return searchType === SearchType.HOTEL_ONLY ? TAB_INDEXES.HOTEL_ONLY : TAB_INDEXES.HOLIDAYS;
  }
  return currentTab || TAB_INDEXES.HOLIDAYS;
}

// Async thunk to handle path changes
export const handlePathChange = createAsyncThunk(
  CommonSearchActionTypes.HANDLE_PATH_CHANGE,
  async (_, { getState }) => {
    const state = getState() as GlobalAppState;
    const path = getPath(state);
    const searchType = getSearchType(state);
    const currentTab = state.search.common.tabIndex;

    return getTabIndexFromPath(path, searchType, currentTab);
  }
);

// Async thunk for setting tab index (if needed for any async operations)
export const setTabIndexAsync = createAsyncThunk(CommonSearchActionTypes.SET_TAB_INDEX, async (tabIndex: number) => {
  // This could include any async operations if needed
  return tabIndex;
});

// Async thunk for setting tab index by search type
export const setTabIndexBySearchTypeAsync = createAsyncThunk(
  CommonSearchActionTypes.SET_TAB_INDEX_BY_SEARCH_TYPE,
  async (searchType: SearchType) => {
    // This could include any async operations if needed
    return searchType === SearchType.HOTEL_ONLY ? TAB_INDEXES.HOTEL_ONLY : TAB_INDEXES.HOLIDAYS;
  }
);

// RTK Slice
const commonSearchSlice = createSlice({
  name: CommonSearchActionTypes.SLICE_NAME,
  initialState: INITIAL_COMMON_SEARCH_STATE,
  reducers: {
    setTabIndex: (state, action: PayloadAction<number>) => {
      state.tabIndex = action.payload;
    },
    setTabIndexBySearchType: (state, action: PayloadAction<SearchType>) => {
      state.tabIndex = action.payload === SearchType.HOTEL_ONLY ? TAB_INDEXES.HOTEL_ONLY : TAB_INDEXES.HOLIDAYS;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(handlePathChange.fulfilled, (state, action) => {
        state.tabIndex = action.payload;
      })
      .addCase(setTabIndexAsync.fulfilled, (state, action) => {
        state.tabIndex = action.payload;
      })
      .addCase(setTabIndexBySearchTypeAsync.fulfilled, (state, action) => {
        state.tabIndex = action.payload;
      });
  }
});

// Export actions
export const { setTabIndex, setTabIndexBySearchType } = commonSearchSlice.actions;

// Export reducer
export const commonSearchReducer = commonSearchSlice.reducer;
